import { NoticeDialogLink } from "@/components/privacy/Notice";
import { useState } from "react";
import { Clock, ShieldCheck, Info, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { deviceInfo } from "@/lib/assessments";
import { PageTour } from "@/components/app/PageTour";

const Point = ({ icon: Icon, title, children }) => (
  <div className="flex gap-3">
    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-tint text-brand"><Icon size={16} /></div>
    <div><p className="font-medium text-ink">{title}</p><p className="text-sm leading-relaxed text-ink-secondary">{children}</p></div>
  </div>
);

export function AssessIntro({ token, info, onStart }) {
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const start = async () => {
    setBusy(true);
    try { await api.post(`/public/assess/${token}/consent`, { version: info.consent_version, device: deviceInfo() }); onStart(); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <article className="panel space-y-6 p-5 sm:p-8" data-tour="pt-assess-intro" data-testid="assess-intro">
      <div className="flex justify-end"><PageTour /></div>
      <div className="space-y-2">
        <p className="text-sm text-ink-muted">{info.job_title}</p>
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{info.item_count ? "Your work-style questionnaire" : "Your assessment"}</h1>
        {info.parts?.length > 0 && (
          <ol className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-ink-secondary" data-testid="assess-intro-parts">
            {info.parts.map((t, i) => <li key={t + i}><span className="font-mono text-ink-muted">{i + 1}.</span> {t}</li>)}
          </ol>
        )}
      </div>
      <div className="space-y-4">
        <Point icon={Clock} title={`About ${info.est_minutes} minutes`}>Answers save after every page, so you can stop and come back with this link {info.expires_at ? `until ${new Date(info.expires_at).toLocaleString()}` : "any time"}.</Point>
        <Point icon={UserCheck} title="Why we ask">{info.item_count ? "It helps the team understand how you like to work and how you approach real legal work. The work-style statements have no right or wrong answers, so answer them honestly; answer the other parts in your own words." : "It helps the team understand your judgment, knowledge and how you approach real legal work. Answer in your own words."}</Point>
        <Point icon={Info} title="How AI is used">{info.ai_note}</Point>
        <Point icon={ShieldCheck} title="Your privacy">Your answers are used only for this application. <a href="/privacy" target="_blank" rel="noreferrer" className="text-brand hover:underline">Read the privacy notice</a>.</Point>
      </div>
      <label className="flex items-start gap-3 rounded-[6px] border border-line bg-subtle p-4 text-sm leading-relaxed text-ink-secondary" data-tour="pt-assess-consent-label" data-testid="assess-consent-label">
        <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" data-testid="assess-consent-checkbox" />
        <span>{info.consent_text} <span className="text-xs text-ink-muted">({info.consent_version})</span> <NoticeDialogLink testId="assess-notice-link" /></span>
      </label>
      <Button className="h-12 w-full text-base sm:w-auto sm:px-8" disabled={!agree || busy} onClick={start} data-tour="pt-assess-start-btn" data-testid="assess-start-btn">
        {info.answered ? "Continue where I left off" : "Start"}
      </Button>
    </article>
  );
}
